import { COLORS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import type { StoredSectorTemplate } from '../api/keycloak-admin/org-config';

/** Format sector slug to display label */
export function formatSector(sector: string): string {
  return sector.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** Group templates by sector for display */
export function groupBySector(
  templates: StoredSectorTemplate[],
): Record<string, StoredSectorTemplate[]> {
  const groups: Record<string, StoredSectorTemplate[]> = {};
  for (const t of templates) {
    const key = t.sector;
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  }
  return groups;
}

interface SectorGroupProps {
  sector: string;
  templates: StoredSectorTemplate[];
  isDark: boolean;
  colors: ColorTheme;
  previewId: string | null;
  confirmDeleteId: string | null;
  onPreview: (id: string | null) => void;
  onClone: (t: StoredSectorTemplate) => void;
  onConfirmDelete: (id: string | null) => void;
  onDelete: (id: string) => void;
  onCancelDelete: () => void;
}

/** A group of templates for a single sector */
export function SectorGroup({
  sector, templates, isDark, colors, previewId, confirmDeleteId,
  onPreview, onClone, onConfirmDelete, onDelete, onCancelDelete,
}: SectorGroupProps): React.JSX.Element {
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
        textTransform: 'uppercase', color: colors.textSecondary,
        marginBottom: '8px', paddingBottom: '4px',
        borderBottom: `1px solid ${colors.border}`,
      }}>
        {formatSector(sector)}
      </div>
      {templates.map((t) => (
        <TemplateRow key={t.id} template={t}
          isDark={isDark} colors={colors}
          isPreview={previewId === t.id}
          isConfirmDelete={confirmDeleteId === t.id}
          onPreview={() => onPreview(previewId === t.id ? null : t.id)}
          onClone={() => onClone(t)}
          onConfirmDelete={() => onConfirmDelete(t.id)}
          onDelete={() => onDelete(t.id)}
          onCancelDelete={onCancelDelete}
        />
      ))}
    </div>
  );
}

/** Single template row with actions */
function TemplateRow({ template, isDark, colors, isPreview, isConfirmDelete, onPreview, onClone, onConfirmDelete, onDelete, onCancelDelete }: {
  template: StoredSectorTemplate;
  isDark: boolean; colors: ColorTheme;
  isPreview: boolean; isConfirmDelete: boolean;
  onPreview: () => void; onClone: () => void;
  onConfirmDelete: () => void; onDelete: () => void;
  onCancelDelete: () => void;
}): React.JSX.Element {
  const smallBtn: React.CSSProperties = {
    padding: '4px 10px', borderRadius: '4px', border: 'none',
    fontFamily: FONTS.body, fontSize: '10px', cursor: 'pointer',
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    color: colors.text,
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 10px', borderRadius: '6px', marginBottom: '4px',
      backgroundColor: isPreview
        ? (isDark ? 'rgba(1,169,237,0.08)' : 'rgba(1,101,198,0.06)')
        : 'transparent',
    }}>
      <span style={{ flex: 1, fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>
        {template.typeName}
      </span>
      {template.builtIn && (
        <span style={{
          padding: '2px 6px', borderRadius: '3px', fontSize: '9px',
          fontFamily: FONTS.heading, letterSpacing: '0.1em', textTransform: 'uppercase',
          backgroundColor: isDark ? 'rgba(1,169,237,0.12)' : 'rgba(1,101,198,0.08)',
          color: COLORS.accent.blue,
        }}>
          Built-in
        </span>
      )}
      <button onClick={onPreview} style={smallBtn}>
        {isPreview ? 'Hide' : 'Preview'}
      </button>
      <button onClick={onClone} style={smallBtn}>Clone</button>
      {!template.builtIn && (
        isConfirmDelete ? (
          <>
            <button onClick={onDelete} style={{ ...smallBtn, backgroundColor: '#EF4444', color: '#FFF' }}>
              Confirm
            </button>
            <button onClick={onCancelDelete} style={smallBtn}>Cancel</button>
          </>
        ) : (
          <button onClick={onConfirmDelete} style={{ ...smallBtn, color: '#EF4444' }}>
            Delete
          </button>
        )
      )}
    </div>
  );
}
